import React, { useEffect, useRef, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";

function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

export default function LetteraGenericLookupModal({
  open = false,
  field,
  letterId,
  onClose,
  onPickItem,
  fetcher,
}) {
  const [query, setQuery] = useState("");
  const debounced = useDebouncedValue(query, 300);

  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const abortRef = useRef(null);

  const loadResults = async (search, pageArg = 1, append = false) => {
    if (!open || !field || typeof fetcher !== "function") return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const data = await fetcher(
        {
          q: search,
          field: field?.placeholder,
          lettera_id: Number(letterId),
          page: pageArg,
          page_size: 20,
        },
        controller.signal
      );

      const nextItems = Array.isArray(data?.results) ? data.results : [];
      const more = !!data?.pagination?.more;

      setItems((prev) => (append ? [...prev, ...nextItems] : nextItems));
      setHasMore(more);
      setPage(pageArg);
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("Generic lookup error:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;

    setQuery("");
    setItems([]);
    setPage(1);
    setHasMore(false);
  }, [open, field?.id]);

  useEffect(() => {
    if (!open) return;
    loadResults(debounced, 1, false);
  }, [debounced, open, field?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  if (!open || !field) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/30 p-4"
      style={{ zIndex: 999 }}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-base font-semibold text-gray-900">
            Seleziona {field?.nome}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Cerca ${field?.nome?.toLowerCase() || ""}...`}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
          />

          <div className="mt-4 max-h-[420px] overflow-auto rounded-lg border border-gray-200">
            {loading && !items.length ? (
              <div className="px-4 py-3 text-sm text-gray-500">Caricamento...</div>
            ) : items.length ? (
              <div className="divide-y divide-gray-100">
                {items.map((item, idx) => (
                  <button
                    key={`${item.id}-${idx}`}
                    type="button"
                    onClick={() => onPickItem?.(item)}
                    className="block w-full px-4 py-3 text-left hover:bg-indigo-50"
                  >
                    <div className="text-sm font-medium text-gray-900">
                      {item.text}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="px-4 py-3 text-sm text-gray-500">
                Nessun risultato
              </div>
            )}
          </div>

          {hasMore && (
            <div className="mt-3 flex justify-center">
              <button
                type="button"
                disabled={loading}
                onClick={() => loadResults(query, page + 1, true)}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
              >
                {loading ? "Caricamento..." : "Carica altri"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}